import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiCpu, FiShield, FiBriefcase, FiZap } = FiIcons;

const Pricing = () => {
  const plans = [
    {
      name: "Builder",
      price: "$0",
      desc: "For individual clinicians and students exploring health AI.",
      features: [
        "Single Workflow Access",
        "Venice-Clinical v4 (Limited)",
        "Standard Audit Logging",
        "Community Marketplace",
        "Public .hd exports"
      ],
      cta: "Start Building",
      icon: FiCpu,
      color: "bg-brand-ivory text-brand-indigo"
    },
    {
      name: "Pro",
      price: "$299",
      period: "/month",
      desc: "For private practices and small engineering teams.",
      features: [
        "Unlimited Workflows",
        "Full Venice-Clinical v4 Access",
        "HF Clinical Mistral-7B",
        "Advanced HIPAA BAA",
        "Private .hdpkg publishing",
        "Priority Email Support"
      ],
      cta: "Go Pro",
      icon: FiZap,
      color: "bg-brand-indigo text-white",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      desc: "For health systems and large-scale tech founders.",
      features: [
        "Dedicated Inference Nodes",
        "Custom Model Training",
        "On-Premise Deployment",
        "Clinical Validation Support",
        "White-glove Compliance",
        "24/7 Dedicated Succes"
      ],
      cta: "Contact Sales",
      icon: FiShield,
      color: "bg-brand-ivory text-brand-indigo"
    }
  ];

  return (
    <div className="min-h-screen bg-brand-ivory pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <div className="mb-16">
          <h1 className="text-6xl font-display font-bold text-brand-indigo mb-6">Simple, Clinical Pricing</h1>
          <p className="text-xl text-brand-slate max-w-2xl mx-auto">Scale your AI operations from single-clinician tools to system-wide health infrastructure.</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative flex flex-col p-10 rounded-[3rem] border border-gray-100 shadow-sm hover:shadow-2xl transition-all ${plan.color}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-brand-mint text-brand-indigo px-6 py-2 rounded-full font-bold text-xs uppercase tracking-widest shadow-lg">
                  Most Popular
                </div>
              )}
              
              <div className="flex-grow">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-8 mx-auto">
                  <SafeIcon icon={plan.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-2">{plan.name}</h3>
                <div className="flex items-end justify-center mb-6">
                  <span className="text-5xl font-bold">{plan.price}</span>
                  {plan.period && <span className="text-sm opacity-60 mb-2 ml-1">{plan.period}</span>}
                </div>
                <p className="text-sm opacity-60 mb-10 leading-relaxed">{plan.desc}</p>
                
                <ul className="space-y-4 text-left mb-12">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start space-x-3 text-sm font-medium">
                      <SafeIcon icon={FiCheck} className="text-brand-mint mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button className={`w-full py-5 rounded-2xl font-bold text-lg transition-all shadow-xl hover:scale-[1.02] active:scale-[0.98] ${plan.name === 'Pro' ? 'bg-brand-mint text-brand-indigo' : 'bg-brand-indigo text-white'}`}>
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-20 p-12 bg-white rounded-[3rem] border border-gray-100 text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h4 className="text-2xl font-display font-bold text-brand-indigo mb-2">Non-Profit or Academic?</h4>
            <p className="text-brand-slate">We offer subsidized access for medical students and charitable health organizations.</p>
          </div>
          <button className="bg-brand-ivory text-brand-indigo px-10 py-4 rounded-2xl font-bold hover:bg-brand-indigo hover:text-white transition-all whitespace-nowrap">
            Apply for Subsidy
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pricing;